import { useState, useEffect } from 'react';
import supabase from '../../supabase-client';

export const useCatalog = (type = 'published') => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar catálogo según tipo
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch books from productbook table
        const { data: booksData, error: booksError } = await supabase
          .from('productbook')
          .select(`
            *,
            authorsbook(
              id_author,
              name_author
            )
          `)
          .order('id_product', { ascending: false });
        
        if (booksError) {
          console.error('Error loading books:', booksError);
        }

        // Fetch bibles from biblebook table
        const { data: biblesData, error: biblesError } = await supabase
          .from('biblebook')
          .select('*')
          .order('id_bible', { ascending: false });

        if (biblesError) {
          console.error('Error loading bibles:', biblesError);
        }

        // Fetch things from thingproduct table
        const { data: thingsData, error: thingsError } = await supabase
          .from('thingproduct')
          .select('*')
          .order('id_thing', { ascending: false });

        if (thingsError) {
          console.error('Error loading things:', thingsError);
        }

        // Process books data
        const processedBooks = (booksData || []).map(book => {
          const authorName = book.authorsbook?.name_author || 'Autor desconocido';
          
          return {
            ...book,
            authorName: authorName,
            author: authorName,
            categoryNames: [],
            product_type: 'book'
          };
        });

        // Process bibles data
        const processedBibles = (biblesData || []).map(bible => ({
          ...bible,
          product_type: 'bible',
          id_product: bible.id_bible, // For compatibility
          bible_id: bible.id_bible,
          favorite_product: bible.favorite_product || false,
          favorite_count_product: bible.favorite_count_product || 0
        }));

        // Process things data
        const processedThings = (thingsData || []).map(thing => ({
          ...thing,
          product_type: 'thing',
          id_product: thing.id_thing, // For compatibility
          thing_id: thing.id_thing,
          favorite_product: thing.favorite_product || false,
          favorite_count_product: thing.favorite_count_product || 0
        }));

        // Combine all products
        const allProducts = [...processedBooks, ...processedBibles, ...processedThings];

        setCatalog(allProducts);
      } catch (err) {
        console.error('Error loading catalog:', err.message || err);
        setError('Error al cargar el catálogo');
      } finally {
        setLoading(false);
      }
    };

    loadCatalog();
  }, [type]);

  // Recargar catálogo
  const publishCatalog = async () => {
    try {
      const { data: booksData, error } = await supabase
        .from('productbook')
        .select(`
          *,
          authorsbook(
            id_author,
            name_author
          )
        `)
        .order('id_product', { ascending: false });
      
      if (error) throw new Error(error.message);
      
      const mappedData = (booksData || []).map(item => {
        const authorName = item.authorsbook?.name_author || 'Autor desconocido';
        
        return {
          ...item,
          authorName: authorName,
          author: authorName,
          categoryNames: []
        };
      });
      
      setCatalog(mappedData);
      return mappedData;
    } catch (err) {
      console.error('Error publishing catalog:', err.message || err);
      throw err;
    }
  };

  // Agregar libro usando inserción directa
  const addBook = async (bookData) => {
    try {
      setError(null);
      
      console.log('🔍 ADD BOOK DEBUG:', {
        bookData,
        title: bookData.title_product || bookData.title,
        author_id: bookData.author_id || bookData.author,
        available: bookData.available_product !== undefined ? bookData.available_product : bookData.available
      });
      
      const insertData = {
        title_product: bookData.title_product || bookData.title,
        rating_product: parseFloat(bookData.rating_product || bookData.ranking) || 0,
        description_product: bookData.description_product || bookData.description,
        price_original_product: parseFloat(bookData.price_original_product || bookData.price) || 0,
        available_product: bookData.available_product !== undefined ? bookData.available_product : (bookData.available !== undefined ? bookData.available : true),
        categories_product: bookData.categories_product || bookData.categories || [],
        favorite_product: bookData.favorite_product || false,
        pages_product: parseInt(bookData.pages_product || bookData.pages) || null,
        language_product: bookData.language_product || bookData.language || null,
        image_url_product: bookData.image_url_product || bookData.images || [],
        author_id: parseInt(bookData.author_id || bookData.author) || null,
        favorite_count_product: bookData.favorite_count_product || 0
      };

      console.log('🔍 INSERT DATA:', insertData);

      const { data, error } = await supabase
        .from('productbook')
        .insert([insertData])
        .select();

      if (error) {
        console.error('❌ INSERT ERROR:', error);
        throw new Error(error.message);
      }
      
      if (!data || data.length === 0) throw new Error('No se pudo crear el libro');

      console.log('✅ BOOK CREATED:', data[0]);

      // Recargar catálogo
      await publishCatalog();
      return data[0];
    } catch (err) {
      console.error('❌ Error adding book:', err);
      setError(err.message);
      throw err;
    }
  };

  // Actualizar libro usando actualización directa
  const updateBook = async (bookId, updateData) => {
    try {
      setError(null);
      
      const updateFields = {};
      
      if (updateData.title_product || updateData.title) updateFields.title_product = updateData.title_product || updateData.title;
      if (updateData.rating_product || updateData.ranking) updateFields.rating_product = parseFloat(updateData.rating_product || updateData.ranking);
      if (updateData.description_product || updateData.description) updateFields.description_product = updateData.description_product || updateData.description;
      if (updateData.price_original_product || updateData.price) updateFields.price_original_product = parseFloat(updateData.price_original_product || updateData.price);
      if (updateData.available_product !== undefined || updateData.available !== undefined) {
        updateFields.available_product = updateData.available_product !== undefined ? updateData.available_product : updateData.available;
      }
      if (updateData.categories_product || updateData.categories) updateFields.categories_product = updateData.categories_product || updateData.categories;
      if (updateData.pages_product || updateData.pages) updateFields.pages_product = parseInt(updateData.pages_product || updateData.pages);
      if (updateData.language_product || updateData.language) updateFields.language_product = updateData.language_product || updateData.language;
      if (updateData.image_url_product || updateData.images) updateFields.image_url_product = updateData.image_url_product || updateData.images;
      if (updateData.author_id || updateData.author) updateFields.author_id = parseInt(updateData.author_id || updateData.author);
      

      console.log('🔍 UPDATE FIELDS DEBUG:', {
        bookId,
        updateFields,
        availableValue: updateFields.available_product
      });

      const { data, error } = await supabase
        .from('productbook')
        .update(updateFields)
        .eq('id_product', bookId)
        .select();

      if (error) throw new Error(error.message);
      if (!data || data.length === 0) throw new Error('No se pudo actualizar el libro');

      // Recargar catálogo
      await publishCatalog();
      return data[0];
    } catch (err) {
      console.error('Error updating book:', err);
      setError(err.message);
      throw err;
    }
  };

  // Eliminar libro usando eliminación directa
  const deleteBook = async (bookId) => {
    try {
      setError(null);
      
      console.log('🔍 DELETE BOOK DEBUG:', { bookId, type: typeof bookId });
      
      const { data, error } = await supabase
        .from('productbook')
        .delete()
        .eq('id_product', parseInt(bookId))
        .select();

      if (error) {
        console.error('❌ DELETE ERROR:', error);
        throw new Error(error.message);
      }

      console.log('✅ BOOK DELETED:', data);

      // Verificar si la tabla está vacía y reiniciar secuencia
      const { data: remainingBooks, error: countError } = await supabase
        .from('productbook')
        .select('id_product', { count: 'exact', head: true });

      if (!countError && remainingBooks === null) {
        // La tabla está vacía, reiniciar la secuencia
        const { error: resetError } = await supabase.rpc('reset_productbook_sequence');
        if (resetError) {
          console.warn('No se pudo reiniciar la secuencia:', resetError);
        } else {
          console.log('✅ Secuencia reiniciada - próximo ID será 1');
        }
      }

      // Recargar catálogo
      await publishCatalog();
      return true;
    } catch (err) {
      console.error('❌ Error deleting book:', err);
      setError(err.message);
      throw err;
    }
  };

  // Actualización masiva
  const updateBooks = async () => {
    try {
      await publishCatalog();
    } catch (err) {
      console.error('Error updating books:', err.message || err);
      throw err;
    }
  };

  // Forzar recarga completa del catálogo
  const refreshCatalog = async () => {
    console.log('🔄 FORCING CATALOG REFRESH');
    setLoading(true);
    try {
      const { data: booksData, error } = await supabase
        .from('productbook')
        .select(`
          *,
          authorsbook(
            id_author,
            name_author
          )
        `)
        .order('id_product', { ascending: false });
      
      if (error) throw new Error(error.message);
      
      const mappedData = (booksData || []).map(item => {
        const authorName = item.authorsbook?.name_author || 'Autor desconocido';
        
        return {
          ...item,
          authorName: authorName,
          author: authorName,
          categoryNames: []
        };
      });
      
      setCatalog(mappedData);
      return mappedData;
    } catch (err) {
      console.error('Error refreshing catalog:', err);
      setError('Error al actualizar el catálogo');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    catalog,
    loading,
    error,
    publishCatalog,
    refreshCatalog,
    addBook,
    updateBook,
    deleteBook,
    updateBooks,
    clearError: () => setError(null),
  };
};
